import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("label", {
  for: "email",
  class: "block text-sm font-medium text-zinc-700"
}, " Email ", -1)
const _hoisted_3 = { class: "mt-1" }
const _hoisted_4 = ["onKeyup"]
const _hoisted_5 = /*#__PURE__*/_createElementVNode("label", {
  for: "password",
  class: "block text-sm font-medium text-zinc-700"
}, " Password ", -1)
const _hoisted_6 = { class: "mt-1" }
const _hoisted_7 = ["onKeyup"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("svg", {
  class: "animate-spin -ml-1 mr-3 h-5 w-5 text-white",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}, [
  /*#__PURE__*/_createElementVNode("circle", {
    class: "opacity-25",
    cx: "12",
    cy: "12",
    r: "10",
    stroke: "currentColor",
    "stroke-width": "4"
  }),
  /*#__PURE__*/_createElementVNode("path", {
    class: "opacity-75",
    fill: "currentColor",
    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
  })
], -1)
const _hoisted_10 = /*#__PURE__*/_createTextVNode(" Signing in ")
const _hoisted_11 = /*#__PURE__*/_createTextVNode(" Sign in ")

import { inject, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { gsap } from 'gsap'
// Store
import { authStore } from '@/store/authStore'
// Other modules
import { SignInStep } from '@/types/'
import { RouteName } from '@/router/RouteName'
// Components
import { ExclamationCircleIcon } from '@heroicons/vue/outline'


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const router = useRouter()

// STATE
const loading = ref(false)

const form = reactive({
  email: {
    value: '',
  },
  password: {
    value: '',
  },
})

const errorMessage = ref('')

// DOM
const errorElement = ref<HTMLElement>()

const setStep = inject<(signInStep: SignInStep) => void>('setStep')

function clearErrors() {
  if (!errorElement.value) return
  gsap.to(errorElement.value, { height: 0, marginTop: 0, opacity: 0, duration: 0.3 })
}

function showErrorMessage(message?: string) {
  if (message) {
    errorMessage.value = message
  }
  if (!errorElement.value) return
  gsap.to(errorElement.value, { height: 'auto', marginTop: '1rem', opacity: 1, duration: 0.3 })
}

async function signIn() {
  clearErrors()
  loading.value = true

  try {
    // store the email
    authStore.setEmail(form.email.value)
    // login
    await authStore.login(form.email.value, form.password.value)

    // Check if this user was manually created directly in AWS. This results in a 'NEW_PASSWORD_REQUIRED' challengeName.
    if (authStore.getState().user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
      if (setStep) {
        setStep('forcedNewPassword')
      }
      return
    }

    // Otherwise, check if validation passed and move them to the dashboard
    if (authStore.getState().isAuthenticated) {
      router.replace({ name: RouteName.dashboard })
    } else {
      throw Error(`User not found.`)
    }
  } catch (err: any) {
    const error = err as any
    console.log(err)
    if (error.code && error.code === 'InvalidParameterException') {
      showErrorMessage('Sign in unsuccessful')
    } else if (error.code === 'PasswordResetRequiredException') {
      if (setStep) {
        setStep('passwordReset')
      }
    } else if (error.message && (error.message as string).includes('quota')) {
      showErrorMessage(
        error.message +
          ` This can happen after updating your device. Please close the app completely, then reopen and try again.`
      )
    } else {
      showErrorMessage(error.message || 'Sign in unsuccessful')
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  clearErrors()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _hoisted_2,
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("input", {
          type: "email",
          id: "email",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).email.value) = $event)),
          onFocus: clearErrors,
          onKeyup: _withKeys(signIn, ["enter"]),
          autocomplete: "email",
          required: "true",
          class: "appearance-none block w-full px-3 py-2 border border-zinc-300 rounded-md shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        }, null, 40, _hoisted_4), [
          [
            _vModelText,
            _unref(form).email.value,
            void 0,
            { trim: true }
          ]
        ])
      ])
    ]),
    _createElementVNode("div", null, [
      _hoisted_5,
      _createElementVNode("div", _hoisted_6, [
        _withDirectives(_createElementVNode("input", {
          id: "password",
          type: "password",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).password.value) = $event)),
          onFocus: clearErrors,
          onKeyup: _withKeys(signIn, ["enter"]),
          class: "appearance-none block w-full px-3 py-2 border border-zinc-300 rounded-md shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        }, null, 40, _hoisted_7), [
          [
            _vModelText,
            _unref(form).password.value,
            void 0,
            { trim: true }
          ]
        ])
      ])
    ]),
    _createElementVNode("button", {
      onClick: signIn,
      disabled: loading.value || !_unref(form).email.value || !_unref(form).password.value,
      class: "w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-opacity"
    }, [
      (loading.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _hoisted_9,
            _hoisted_10
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _hoisted_11
          ], 64))
    ], 8, _hoisted_8),
    _createElementVNode("p", {
      ref: (_value, _refs) => {
        _refs['errorElement'] = _value
        errorElement.value = _value
      },
      role: "alert",
      class: "text-red-500 text-sm mt-0 flex flex-row items-center space-x-2 opacity-0 h-0"
    }, [
      _createVNode(_unref(ExclamationCircleIcon), { class: "h-6 w-6" }),
      _createElementVNode("span", null, _toDisplayString(errorMessage.value), 1)
    ], 512)
  ]))
}
}

})